import React, { useState } from 'react';
import { Table, Tag, Icon, Button, Tooltip } from 'antd';

const UserAnalyticsTable = ({ data, loading }) => {
  const [sortedInfo, setSortedInfo] = useState({});

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const exportToCSV = () => {
    if (!data || data.length === 0) return;

    const headers = ['User ID', 'Total Requests', 'Avg Response Time (ms)', 'Total Cost ($)', 'Last Request', 'Most Used Endpoint'];
    const rows = data.map(user => [
      user.user_id || '-',
      user.total_requests || 0,
      (user.avg_response_time || 0).toFixed(2),
      (user.total_cost || 0).toFixed(3),
      formatDate(user.last_request),
      user.most_used_endpoint || '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `user-analytics-${new Date().toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'user_id',
      key: 'user_id',
      sorter: (a, b) => (a.user_id || '').localeCompare(b.user_id || ''),
      sortOrder: sortedInfo.columnKey === 'user_id' && sortedInfo.order,
      render: (userId) => {
        const isEmail = userId && userId.includes('@');
        return (
          <div>
            <Icon type={isEmail ? 'mail' : 'user'} style={{ marginRight: 8, color: '#1890ff' }} />
            <span style={{ fontWeight: 500 }}>{userId || 'Unknown'}</span>
          </div>
        );
      },
    },
    {
      title: 'Total Requests',
      dataIndex: 'total_requests',
      key: 'total_requests',
      sorter: (a, b) => (a.total_requests || 0) - (b.total_requests || 0),
      sortOrder: sortedInfo.columnKey === 'total_requests' && sortedInfo.order,
      render: (count) => (
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
          {(count || 0).toLocaleString()}
        </Tag>
      ),
    },
    {
      title: 'Avg Response Time',
      dataIndex: 'avg_response_time',
      key: 'avg_response_time',
      sorter: (a, b) => (a.avg_response_time || 0) - (b.avg_response_time || 0),
      sortOrder: sortedInfo.columnKey === 'avg_response_time' && sortedInfo.order,
      render: (time) => {
        const ms = time || 0;
        const color = ms < 1000 ? 'green' : ms < 3000 ? 'orange' : 'red';
        return (
          <Tag color={color}>
            <Icon type="clock-circle" /> {ms.toFixed(0)}ms
          </Tag>
        );
      },
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      sorter: (a, b) => (a.total_cost || 0) - (b.total_cost || 0),
      sortOrder: sortedInfo.columnKey === 'total_cost' && sortedInfo.order,
      render: (cost) => {
        const amount = cost || 0;
        return (
          <span style={{ fontWeight: 600, color: amount > 1 ? '#f5222d' : '#52c41a' }}>
            ${amount.toFixed(3)}
          </span>
        );
      },
    },
    {
      title: 'Last Request',
      dataIndex: 'last_request',
      key: 'last_request',
      sorter: (a, b) => {
        const dateA = a.last_request ? new Date(a.last_request).getTime() : 0;
        const dateB = b.last_request ? new Date(b.last_request).getTime() : 0;
        return dateA - dateB;
      },
      sortOrder: sortedInfo.columnKey === 'last_request' && sortedInfo.order,
      render: (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(timestamp);
        const now = new Date();
        const diffMinutes = Math.floor((now - date) / (1000 * 60));
        
        let timeAgo;
        if (diffMinutes < 1) {
          timeAgo = 'Just now';
        } else if (diffMinutes < 60) {
          timeAgo = `${diffMinutes}m ago`;
        } else if (diffMinutes < 1440) {
          timeAgo = `${Math.floor(diffMinutes / 60)}h ago`;
        } else {
          timeAgo = `${Math.floor(diffMinutes / 1440)}d ago`;
        }
        
        return (
          <Tooltip title={date.toLocaleString()}>
            <span style={{ color: '#666' }}>
              <Icon type="calendar" style={{ marginRight: 4 }} />
              {timeAgo}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Most Used Endpoint',
      dataIndex: 'most_used_endpoint',
      key: 'most_used_endpoint',
      render: (endpoint) => {
        if (!endpoint || endpoint === '-') return '-';
        
        // Extract endpoint name for display
        const endpointName = endpoint.split('/').pop() || endpoint;
        const endpointType = endpoint.includes('suggest-questions') ? 'Question Suggestions' :
                            endpoint.includes('optimize-order') ? 'Question Ordering' :
                            endpoint.includes('suggest-followups') ? 'Follow-up Suggestions' :
                            endpointName;
        
        return (
          <Tooltip title={endpoint}>
            <Tag color="purple" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <Icon type="api" style={{ marginRight: 4 }} />
              {endpointType}
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
            {data?.length || 0} users tracked
          </Tag>
        </div>
        <Button 
          type="primary" 
          icon="download" 
          onClick={exportToCSV}
          disabled={!data || data.length === 0}
        >
          Export to CSV
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(record) => record.user_id || Math.random()}
        onChange={handleChange}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
        }}
        scroll={{ x: 1000 }}
        size="middle"
      />
    </div>
  );
};

export default UserAnalyticsTable;
